import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import BookStageBar from '../../components/books/BookStageBar';
import { PIPELINE, STAGE_AR, STAGE_BADGE } from '../../components/books/BookStatus';
import { fmtDateShort } from '../../utils/dates';

const emptyForm = {
  project_id: '', book_title: '', code: '', book_author: '', book_edition: '', book_pages: '', description: '',
};

const emptyPerson = { project_id: '', name: '', user_id: '', can_summarize: true, can_review: false };

export default function BookSummaries() {
  const { api, user } = useAuth();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [stageTotals, setStageTotals] = useState({});
  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [stage, setStage] = useState('');
  const [projectId, setProjectId] = useState('');
  const [personId, setPersonId] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [term, setTerm] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [showTeam, setShowTeam] = useState(false);
  const [personForm, setPersonForm] = useState(emptyPerson);

  const canManage = user?.permissions?.includes('book_summaries.manage');

  // toast مستثنى من الاعتماديات: هويّته تتغيّر فتعيد الجلب بلا نهاية عند الفشل
  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (stage) qs.set('status', stage);
    if (projectId) qs.set('project_id', projectId);
    if (personId) qs.set('person_id', personId);
    if (overdueOnly) qs.set('overdue', '1');
    if (term.trim()) qs.set('q', term.trim());

    setLoading(true);
    api(`/book-summaries?${qs}`)
      .then(data => {
        setRows(data.summaries || []);
        setStageTotals(data.stage_totals || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, stage, projectId, personId, overdueOnly, term]);

  useEffect(() => { load(); }, [load]);

  const loadPeople = useCallback(() => {
    api('/book-summaries/people').then(setPeople).catch(() => {});
  }, [api]);

  useEffect(() => {
    api('/projects').then(setProjects).catch(() => {});
    loadPeople();
    if (canManage) api('/users').then(setUsers).catch(() => {});
  }, []);

  const openNew = () => {
    setForm({ ...emptyForm, project_id: projectId || projects[0]?.id || '' });
    setShowModal(true);
  };

  const save = async () => {
    try {
      await api('/book-summaries', {
        method: 'POST',
        body: JSON.stringify({
          project_id: Number(form.project_id),
          book_title: form.book_title.trim(),
          code: form.code.trim() || null,
          book_author: form.book_author.trim() || null,
          book_edition: form.book_edition.trim() || null,
          book_pages: form.book_pages ? Number(form.book_pages) : null,
          description: form.description.trim() || null,
        }),
      });
      toast.success('أُضيف الكتاب');
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const savePerson = async () => {
    try {
      await api('/book-summaries/people', {
        method: 'POST',
        body: JSON.stringify({
          project_id: Number(personForm.project_id),
          name: personForm.name.trim(),
          user_id: personForm.user_id ? Number(personForm.user_id) : null,
          can_summarize: personForm.can_summarize,
          can_review: personForm.can_review,
        }),
      });
      toast.success('أُضيف إلى الفريق');
      setPersonForm({ ...emptyPerson, project_id: personForm.project_id });
      loadPeople();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const linkPerson = async (person, userId) => {
    try {
      await api(`/book-summaries/people/${person.id}`, {
        method: 'PUT',
        body: JSON.stringify({ user_id: userId ? Number(userId) : null }),
      });
      toast.success(userId ? 'رُبط الشخص بحسابه' : 'أُلغي الربط');
      loadPeople();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const overdueCount = rows.filter(r => r.is_overdue).length;
  const formValid = form.project_id && form.book_title.trim();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>تلخيص الكتب</h2>
        <div style={{ marginRight: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Link className="btn btn-outline" to="/book-summaries/report">📅 التقرير الشهري</Link>
          {canManage && (
            <>
              <button className="btn btn-outline" onClick={() => setShowTeam(true)}>👥 فريق العمل</button>
              <button className="btn btn-primary" data-tour="books-create" onClick={openNew}>+ كتاب جديد</button>
            </>
          )}
        </div>
      </div>

      {/* عدّادات المراحل قابلة للنقر — تُغني عن لوحة الأعمدة بلا كلفتها */}
      <div className="filters" style={{ gap: 8, marginBottom: 12 }}>
        <button
          className={`btn btn-sm ${stage === '' && !overdueOnly ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => { setStage(''); setOverdueOnly(false); }}
        >
          الكل ({rows.length})
        </button>
        {PIPELINE.map(key => (
          <button
            key={key}
            className={`btn btn-sm ${stage === key ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => { setStage(stage === key ? '' : key); setOverdueOnly(false); }}
          >
            {STAGE_AR[key]} ({stageTotals[key] || 0})
          </button>
        ))}
        <button
          className={`btn btn-sm ${stage === 'approved' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => { setStage(stage === 'approved' ? '' : 'approved'); setOverdueOnly(false); }}
        >
          {STAGE_AR.approved} ({stageTotals.approved || 0})
        </button>
        <button
          className={`btn btn-sm ${overdueOnly ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => { setOverdueOnly(!overdueOnly); setStage(''); }}
          style={{ color: overdueOnly ? undefined : 'var(--danger)' }}
        >
          متأخّر ({overdueCount})
        </button>
      </div>

      <div className="filters" style={{ marginBottom: 12 }}>
        <input
          className="form-input"
          placeholder="ابحث باسم الكتاب أو مؤلّفه أو رمزه"
          value={term}
          onChange={e => setTerm(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <select className="form-select" value={projectId} onChange={e => setProjectId(e.target.value)}>
          <option value="">كل المشاريع</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <select className="form-select" value={personId} onChange={e => setPersonId(e.target.value)}>
          <option value="">كل الفريق</option>
          {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="skeleton-grid">
          <div className="skeleton-card" />
          <div className="skeleton-card" />
          <div className="skeleton-card" />
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty-icon">📚</div>
            لا توجد كتب مطابقة
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }} data-tour="books-table">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الكتاب</th>
                  <th style={{ width: 150 }}>المرحلة</th>
                  <th style={{ width: 130 }}>المسار</th>
                  <th>الباحث</th>
                  <th>المراجع</th>
                  <th style={{ width: 120 }}>المهلة</th>
                  <th style={{ width: 80 }}>معلّق</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/book-summaries/${row.id}`} style={{ fontWeight: 600 }}>{row.book_title}</Link>
                      <div style={{ fontSize: 12, color: 'var(--text-faint)' }}>
                        {[row.code, row.book_author].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${STAGE_BADGE[row.status]}`}>{STAGE_AR[row.status]}</span>
                      {row.rounds_count > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>الجولة {row.round_no}</div>
                      )}
                    </td>
                    <td><BookStageBar summary={row} compact /></td>
                    <td style={{ fontSize: 13 }}>{row.researcher_name || '—'}</td>
                    <td style={{ fontSize: 13 }}>{row.reviewer_name || '—'}</td>
                    <td style={{ fontSize: 13, color: row.is_overdue ? 'var(--danger)' : undefined }}>
                      {row.stage_due_date ? fmtDateShort(row.stage_due_date) : '—'}
                    </td>
                    <td>
                      {row.pending_notes_count > 0
                        ? <span className="badge badge-revision">{row.pending_notes_count}</span>
                        : <span style={{ color: 'var(--text-faint)' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">كتاب جديد للتلخيص</div>
            <div className="form-group">
              <label className="form-label">المشروع *</label>
              <select
                className="form-select"
                value={form.project_id}
                onChange={e => setForm({ ...form, project_id: e.target.value })}
              >
                <option value="">اختر المشروع</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">اسم الكتاب *</label>
              <input
                className="form-input"
                value={form.book_title}
                onChange={e => setForm({ ...form, book_title: e.target.value })}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">المؤلّف</label>
                <input
                  className="form-input"
                  value={form.book_author}
                  onChange={e => setForm({ ...form, book_author: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">الرمز</label>
                <input
                  className="form-input"
                  value={form.code}
                  onChange={e => setForm({ ...form, code: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">الطبعة</label>
                <input
                  className="form-input"
                  value={form.book_edition}
                  onChange={e => setForm({ ...form, book_edition: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">عدد الصفحات</label>
                <input
                  className="form-input"
                  type="number"
                  min="1"
                  value={form.book_pages}
                  onChange={e => setForm({ ...form, book_pages: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">وصف</label>
              <textarea
                className="form-textarea"
                rows={3}
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>إلغاء</button>
              <button className="btn btn-primary" onClick={save} disabled={!formValid}>حفظ</button>
            </div>
          </div>
        </div>
      )}

      {showTeam && (
        <div className="modal-overlay" onClick={() => setShowTeam(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="modal-title">فريق العمل: الباحثون ولجنة المراجعة</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
              أضف الشخص باسمه الآن ولو لم يكن له حساب في المنصّة. ومتى أُعطي حساباً،
              اربطه هنا فيُنسب إليه كل عمله السابق دون أي تعديل آخر.
            </div>

            <div className="form-row" style={{ alignItems: 'flex-end' }}>
              <div className="form-group" style={{ flex: '0 0 170px' }}>
                <label className="form-label">المشروع *</label>
                <select
                  className="form-select"
                  value={personForm.project_id}
                  onChange={e => setPersonForm({ ...personForm, project_id: e.target.value })}
                >
                  <option value="">اختر</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">الاسم *</label>
                <input
                  className="form-input"
                  value={personForm.name}
                  onChange={e => setPersonForm({ ...personForm, name: e.target.value })}
                />
              </div>
              <div className="form-group" style={{ flex: '0 0 130px' }}>
                <label className="form-label">الحساب (إن وُجد)</label>
                <select
                  className="form-select"
                  value={personForm.user_id}
                  onChange={e => setPersonForm({ ...personForm, user_id: e.target.value })}
                >
                  <option value="">بلا حساب</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: '0 0 auto' }}>
                <label className="form-label">الدور</label>
                <div style={{ display: 'flex', gap: 10, fontSize: 13 }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={personForm.can_summarize}
                      onChange={e => setPersonForm({ ...personForm, can_summarize: e.target.checked })}
                    /> باحث
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={personForm.can_review}
                      onChange={e => setPersonForm({ ...personForm, can_review: e.target.checked })}
                    /> مراجع
                  </label>
                </div>
              </div>
              <div className="form-group">
                <button
                  className="btn btn-primary"
                  onClick={savePerson}
                  disabled={!personForm.project_id || !personForm.name.trim()}
                >
                  إضافة
                </button>
              </div>
            </div>

            <div className="table-wrap" style={{ marginTop: 8 }}>
              <table>
                <thead>
                  <tr>
                    <th>الاسم</th>
                    <th style={{ width: 110 }}>الدور</th>
                    <th style={{ width: 180 }}>الحساب</th>
                  </tr>
                </thead>
                <tbody>
                  {people.length === 0 ? (
                    <tr><td colSpan={3} style={{ color: 'var(--text-faint)' }}>لا أحد بعد</td></tr>
                  ) : people.map(p => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td style={{ fontSize: 12 }}>
                        {[p.can_summarize && 'باحث', p.can_review && 'مراجع'].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td>
                        <select
                          className="form-select"
                          value={p.user_id || ''}
                          onChange={e => linkPerson(p, e.target.value)}
                        >
                          <option value="">بلا حساب</option>
                          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowTeam(false)}>إغلاق</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
